import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Monogram initials for an avatar fallback. Per avatar-guidance §A5.1: two
// initials — first letter of the FIRST word + first letter of the LAST word,
// uppercase, skipping any middle names (so "Sarah Jane Parker" → "SP", not
// "SJ"). A single-word name yields its first two letters ("Costco" → "CO").
// Returns "" for a blank/whitespace-only name so the caller can fall back to a
// non-person glyph (an empty monogram would imply an unknown human actor).
export function getInitials(name: string | null | undefined): string {
  if (!name) return "";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase();
  }
  const first = words[0]![0] ?? "";
  const last = words[words.length - 1]![0] ?? "";
  return (first + last).toUpperCase();
}

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Format a date-only value (YYYY-MM-DD) without a timezone shift. `new Date()`
// parses bare ISO dates as UTC midnight, which renders the previous calendar
// day in negative-offset timezones (most of the US) — wrong for values that
// are inherently local, like an appointment's scheduled date (Codex P2,
// ADV-1143). Values that carry a time component fall through to formatDate.
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return formatDate(value);
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return "0";
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatCompactCurrency(
  value: number | null | undefined,
): string {
  if (value == null || value === 0) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumSignificantDigits: 3,
  }).format(value);
}
